import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { AttributeValueModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import { ProductListProductImageStyles } from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import Clickable from "@insite/mobius/Clickable";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import sortBy from "lodash/sortBy";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    products: ProductModel[];
}

const mapStateToProps = (state: ApplicationState) => ({
    productInfosByProductId: state.pages.productList.productInfosByProductId,
    isSearchPage: state.pages.productList.isSearchPage
});

type Props = ReturnType<typeof mapStateToProps> & ProductCardSelections & OwnProps;
export interface ProductListTableStyles {
    wrapper?: InjectableCss;
    dataTable?: DataTableProps;
    descriptionHeader?: DataTableHeaderProps;
    attributesHeader?: DataTableHeaderProps;
    availabilityHeader?: DataTableHeaderProps;
    priceHeader?: DataTableHeaderProps;
    unitOfMeasureHeader?: DataTableHeaderProps;
    actionsHeader?: DataTableHeaderProps;
    descriptionCell?: DataTableCellProps;
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    productImageStyles?: ProductListProductImageStyles;
    attributesCell?: DataTableCellProps;
    availabilityCell?: DataTableCellProps;
    availabilityStyles?: ProductAvailabilityStyles;
    priceCell?: DataTableCellProps;
    productPrice?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    unitOfMeasureCell?: DataTableCellProps;
    unitOfMeasureText?: TypographyPresentationProps;
    actionsCell?: DataTableCellProps;
    sponsoredText?: TypographyPresentationProps;
    productNumberText?: InjectableCss;
}

export const actionsStyles: ProductListTableStyles = {
    wrapper: {
        css: css`
            overflow: auto;
            width: 100%;
        `,
    },
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
            min-width: 320px; /* prevent overlap with second column */
        `,
    },
    leftColumnGridItem: {
        width: 12,
        css: css`
            padding-right: 12px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
        css: css`
            flex-direction: column;
        `,
    },
    productImageStyles: {
        compareCheckbox: {
            css: css`
                align-self: start;
                padding-top: 15px;
            `,
        },
    },
    actionsHeader: {
        css: css`
            position: sticky;
            right: 0;
            width: 200px;
            background-clip: padding-box;
        `,
    },
    descriptionCell: {
        css: css`
            white-space: normal;
            color: ${COLORS.black};
            background: ${COLORS.white};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            height: 40px !important;
            width: 150px;
            span {
                font-size: ${TYPOGRAPHY.size.small};
            }
        `,
    },
    attributesCell: {
        css: css`
            white-space: normal;
            background: ${COLORS.white};
            color: ${COLORS.black};
            font-weight: ${TYPOGRAPHY.weight.base};
            height: 40px !important;
            span {
                font-size: ${TYPOGRAPHY.size.smaller};
            }
        `,
    },
    availabilityCell: {
        css: css`
            white-space: normal;
        `,
    },
    availabilityStyles: {
        messageText: {
            css: css`
                white-space: nowrap;
            `,
        },
    },
    actionsCell: {
        css: css`
            position: sticky;
            right: 0;
            width: 200px;
            background-color: inherit;
            background-clip: padding-box;
        `,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
    attributesHeader: {
        css: css`
            color: ${getColor("secondary")};
            font-family: ${TYPOGRAPHY.fontFamily.base};
            background: ${COLORS.white};
            border: 0;
            span {
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
            }
        `,
    },
    descriptionHeader: {
        css: css`
            color: ${getColor("secondary")};
        `,
    },
};

const styles = actionsStyles;

const ProductListTable = ({ products, productInfosByProductId, isSearchPage }: Props) => {
    if (products.length === 0) {
        return null;
    }

    const getAttributeValues = (attributeValues: AttributeValueModel[] | null) => {
        if (!attributeValues) {
            return [];
        }
        const attributes = attributeValues.map((attribute: AttributeValueModel) => attribute.valueDisplay);
        return attributes;
    };

    const { tableHeaders } = products.reduce<{ attrTypes: AttributeTypeModel[]; tableHeaders: string[] }>(
        (pValue, product) => {
            if (product.attributeTypes && product.attributeTypes.length > pValue.attrTypes.length) {
                return {
                    attrTypes: product.attributeTypes,
                    tableHeaders: sortBy<AttributeTypeModel>(product.attributeTypes, ["sortOrder"])
                        .slice(0, 5)
                        .map(attr => attr.label),
                };
            }
            return pValue;
        },
        { attrTypes: [], tableHeaders: [] },
    );

    const productListRows = products.map((product, idx) => {
        const productInfo = productInfosByProductId[product.id];
        if (!productInfo) {
            return;
        }

        return (
            <ProductListProductContext product={product} key={product.id} idx={idx}>
                <DataTableRow key={product.id} data-test-selector={`productListProductCard${product.id}`}>
                    <DataTableCell {...styles.descriptionCell}>
                        <Typography {...styles.productNumberText}>
                            <Clickable href={productInfo.productDetailPath} data-test-selector="productNumber">
                                {product.productNumber}
                            </Clickable>
                        </Typography>
                    </DataTableCell>
                    {product?.attributeTypes &&
                        tableHeaders?.map((tableheader, ind: number) => {
                            if (product?.attributeTypes) {
                                const productAttributes = product?.attributeTypes.find(
                                    attr => attr.label === tableheader,
                                );
                                return (
                                    <DataTableCell
                                        key={`${productAttributes?.id}-${tableheader}`}
                                        {...styles.attributesCell}
                                    >
                                        {tableheader === productAttributes?.label
                                            ? getAttributeValues(productAttributes.attributeValues).join(", ")
                                            : ""}
                                    </DataTableCell>
                                );
                            }
                            return <DataTableCell key={`empty-cell${ind}`} {...styles.attributesCell}></DataTableCell>;
                        })}
                </DataTableRow>
            </ProductListProductContext>
        );
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            <DataTable {...styles.dataTable}>
                <DataTableHead>
                    <DataTableHeader {...styles.attributesHeader}>{isSearchPage ? translate("Products"): translate("Model")}</DataTableHeader>
                    {tableHeaders.map(header => (
                        <DataTableHeader key={`${header}_tableHeader`} {...styles.attributesHeader}>
                            {header}
                        </DataTableHeader>
                    ))}
                </DataTableHead>
                <DataTableBody>{productListRows}</DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductListTable);
